import { cn } from "@gettactic/helpers";
import {
  FileText,
  Film,
  ImageIcon,
  Pencil,
  Trash2,
  Upload
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Button } from "@cmp-src/primitives/button";

export interface UploadDropzoneProps {
  value?: { id: string; url: string }[];
  onChange?: (files: File[]) => void;
  onUpload?: (
    file: File,
    setProgress: (percent: number) => void
  ) => Promise<void>;
  onUploadComplete?: (successful: File[], failed: File[]) => void;
  accept?: string | string[];
  allowMultiple?: boolean;
  className?: string;
  type?: "images" | "vector-images" | "all-images" | "videos" | "documents" | "pdf";
  autoUpload?: boolean;
  maxSizeMB?: number;
  hint?: string;
}

export interface UploadStatus {
  file?: File;
  id?: string;
  url?: string;
  progress: number;
  status: "idle" | "uploading" | "done" | "error";
  errorMessage?: string;
}

const typeToAcceptMap: Record<
  NonNullable<UploadDropzoneProps["type"]>,
  string[]
> = {
  images: ["image/jpeg", "image/png", "image/webp", "image/gif"],
  "vector-images": ["image/svg+xml"],
  "all-images": [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/gif",
    "image/svg+xml"
  ],
  videos: [
    "video/mp4",
    "video/webm",
    "video/ogg",
    "video/quicktime",
    "video/x-msvideo"
  ],
  documents: [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  ],
  pdf: ["application/pdf"]
};

const typeToHintMap: Record<NonNullable<UploadDropzoneProps["type"]>, string> =
  {
    images: "PNG, JPG, GIF, WEBP",
    "vector-images": "SVG only",
    "all-images": "PNG, JPG, GIF, WEBP, SVG",
    videos: "MP4, WEBM, OGG, MOV, AVI",
    documents: "PDF, DOC, DOCX, PPT, XLSX",
    pdf: "PDF only"
  };

const TypeIcon: React.FC<{
  type?: UploadDropzoneProps["type"];
  className?: string;
}> = ({ type, className }) => {
  const iconClassName = cn("h-12 w-12 text-neutral-400", className);

  switch (type) {
    case "images":
    case "vector-images":
    case "all-images":
      return <ImageIcon className={iconClassName} />;
    case "videos":
      return <Film className={iconClassName} />;
    case "documents":
    case "pdf":
      return <FileText className={iconClassName} />;
    default:
      return <Upload className={iconClassName} />;
  }
};

export const UploadDropzone: React.FC<UploadDropzoneProps> = ({
  value,
  onChange,
  onUpload,
  onUploadComplete,
  accept,
  allowMultiple = true,
  className = "",
  type,
  autoUpload = false,
  maxSizeMB = 10,
  hint
}) => {
  const [uploads, setUploads] = useState<UploadStatus[]>([]);

  useEffect(() => {
    if (value && value.length > 0) {
      setUploads(
        value.map((v) => ({
          id: v.id,
          url: v.url,
          progress: 100,
          status: "done"
        }))
      );
    } else {
      setUploads([]);
    }
  }, [value]);

  const resolvedAccept = accept ?? (type ? typeToAcceptMap[type] : "*");
  const isUploading = uploads.some((u) => u.status === "uploading");

  const updateProgress = (file: File, percent: number) => {
    setUploads((prev) =>
      prev.map((u) =>
        u.file === file
          ? { ...u, progress: Math.max(0, Math.min(percent, 100)) }
          : u
      )
    );
  };

  const updateStatus = (
    file: File,
    status: UploadStatus["status"],
    errorMessage?: string
  ) => {
    setUploads((prev) =>
      prev.map((u) =>
        u.file === file
          ? {
              ...u,
              status,
              progress:
                status === "done" && u.progress < 100 ? 100 : u.progress,
              errorMessage
            }
          : u
      )
    );
  };

  const startUpload = async (uploadItems: UploadStatus[]) => {
    if (!onUpload) return;

    const successful: File[] = [];
    const failed: File[] = [];

    for (const item of uploadItems) {
      const file = item.file;
      if (!file) continue;

      updateProgress(file, 0);
      updateStatus(file, "uploading");

      try {
        await onUpload(file, (progress) => updateProgress(file, progress));
        updateStatus(file, "done");
        successful.push(file);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Upload failed";
        updateStatus(file, "error", errorMessage);
        failed.push(file);
      }
    }

    if (onUploadComplete) {
      onUploadComplete(successful, failed);
    }
  };

  const handleDrop = (files: File[]) => {
    if (isUploading) return;

    const initial: UploadStatus[] = files.map((file) => ({
      file,
      url: file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : undefined,
      progress: 0,
      status: autoUpload ? "uploading" : "idle"
    }));

    const newUploads = allowMultiple ? [...uploads, ...initial] : initial;

    setUploads(newUploads);
    onChange?.(newUploads.map((u) => u.file!).filter(Boolean));

    if (autoUpload) {
      void startUpload(initial);
    }
  };

  const handleRemove = (target: UploadStatus) => {
    const updated = uploads.filter((u) =>
      u.file ? u.file !== target.file : u.id !== target.id
    );
    setUploads(updated);
    onChange?.(updated.map((u) => u.file!).filter(Boolean));
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop: handleDrop,
    accept: resolvedAccept,
    multiple: allowMultiple,
    disabled: isUploading,
    noClick: false,
    noKeyboard: false
  });

  const renderSelectedFiles = () => (
    <div className="flex flex-col items-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
        <TypeIcon type={type} className="h-6 w-6 text-primary" />
      </div>
      <div className="mt-4 space-y-1">
        {uploads.map((u, index) => (
          <p key={u.id || u.file?.name || index} className="text-sm font-medium text-neutral-900">
            {u.file?.name || u.id || "File"}
          </p>
        ))}
      </div>
      <p className="mt-1 text-xs text-primary">File selected</p>
    </div>
  );

  const renderFileList = () => (
    <div className="space-y-2 mt-4">
      {uploads.map((u, index) => (
        <div key={u.id || u.file?.name || index}>
          <div className="flex justify-between items-center mb-1">
            <p className="text-sm text-neutral-700">
              {u.file?.name || u.id || "Image"}
            </p>
          </div>
          <div className="w-full bg-neutral-200 rounded h-2 overflow-hidden relative">
            {u.progress === 0 && u.status === "uploading" ? (
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-400 to-blue-600 animate-pulse" />
            ) : (
              <div
                className={`h-2 rounded transition-all duration-200 ${
                  u.status === "done"
                    ? "bg-green-500"
                    : u.status === "error"
                      ? "bg-red-500"
                      : "bg-blue-500"
                }`}
                style={{ width: `${u.progress}%` }}
              />
            )}
          </div>
          {u.status === "error" && u.errorMessage && (
            <p className="mt-1 text-sm text-red-600">{u.errorMessage}</p>
          )}
        </div>
      ))}
    </div>
  );

  const renderSingleImagePreview = () => {
    const u = uploads[0];
    const imageUrl = u?.url;
    if (!imageUrl) return null;

    const handleChangeClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      open();
    };

    const handleRemoveClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      handleRemove(u);
    };

    return (
      <div className="flex flex-col items-center gap-3">
        <div className="overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50 shadow-sm">
          <img
            src={imageUrl}
            alt="Preview"
            className="max-h-40 w-auto object-contain"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleChangeClick}
            iconLeft={Pencil}
          >
            Change
          </Button>
          <Button
            type="button"
            variant="destructiveOutline"
            size="sm"
            onClick={handleRemoveClick}
            iconLeft={Trash2}
          >
            Remove
          </Button>
        </div>
      </div>
    );
  };

  const hintText =
    hint || (type ? `${typeToHintMap[type]} (max. ${maxSizeMB}MB)` : undefined);

  const hasUploads = uploads.length > 0;

  return (
    <div className={cn("space-y-4", className)}>
      <div
        {...getRootProps()}
        className={cn(
          "rounded-xl border-2 border-dashed p-8 text-center transition-all",
          isUploading
            ? "pointer-events-none cursor-not-allowed border-neutral-300 bg-neutral-50 opacity-50"
            : hasUploads && !onUpload
              ? "cursor-pointer border-primary bg-primary/5 hover:border-primary hover:bg-primary/10"
              : "cursor-pointer border-neutral-300 bg-white hover:border-neutral-400 hover:bg-neutral-50"
        )}
      >
        <input {...getInputProps()} />

        {hasUploads ? (
          <>
            {!allowMultiple && uploads.length === 1 && uploads[0]?.url
              ? renderSingleImagePreview()
              : !onUpload
                ? renderSelectedFiles()
                : renderFileList()}
          </>
        ) : (
          <div className="flex flex-col items-center">
            <TypeIcon type={type} />
            <p className="mt-4 text-sm">
              <span className="font-medium text-primary">Upload a file</span>
              <span className="text-neutral-500"> or drag and drop</span>
            </p>
            {hintText && (
              <p className="mt-1 text-xs text-neutral-400">{hintText}</p>
            )}
          </div>
        )}
      </div>

      {onUpload && !autoUpload && uploads.some((u) => u.status === "idle") && (
        <div className="flex items-center gap-2">
          <Button
            onClick={() =>
              startUpload(uploads.filter((u) => u.status === "idle"))
            }
          >
            Start Upload
          </Button>
        </div>
      )}
    </div>
  );
};
